import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Button,
  Skeleton,
  Card,
  PageHeader,
  Badge,
  WaitingForFirstError,
  AppSettingsModal,
  MuteErrorModal,
  CronMonitorsList,
  FiltersList,
  SourceMapsList,
  RegionalStatusMap,
} from '../components'
import { MetricsDashboard } from '../components/metrics'
import { appsApi, errorsApi, pingsApi, teamsApi } from '../api'
import type { TeamMember } from '../api/teams'
import type { App, ErrorGroup, Ping, UptimeStats, ErrorGroupStatus, AppUpdate } from '../types'
import { ERROR_GROUP_STATUSES } from '../types'
import { useAuth } from '@roselabs-io/saas-shell'
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Activity,
  Zap,
  Filter,
  BellOff,
  EyeOff,
  Settings,
  ChevronRight,
  RotateCcw,
  CheckSquare,
  Square,
  User,
  UserCheck,
  Users,
  FileCode,
} from 'lucide-react'
import toast from 'react-hot-toast'

const STATUS_LABELS: Record<ErrorGroupStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
  ignored: 'Ignored',
  muted: 'Muted',
  regressed: 'Regressed',
}

const STATUS_COLORS: Record<ErrorGroupStatus, string> = {
  open: 'bg-red-100 text-red-700',
  resolved: 'bg-green-100 text-green-700',
  ignored: 'bg-slate-100 text-slate-600',
  muted: 'bg-amber-100 text-amber-700',
  regressed: 'bg-orange-100 text-orange-700',
}

export default function AppDetailPage() {
  const { teamId, id } = useParams<{ teamId: string; id: string }>()
  const { user, isLoading: authLoading } = useAuth()
  const [app, setApp] = useState<App | null>(null)
  const [errorGroups, setErrorGroups] = useState<ErrorGroup[]>([])
  const [pings, setPings] = useState<Ping[]>([])
  const [uptimeStats, setUptimeStats] = useState<UptimeStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isPinging, setIsPinging] = useState(false)
  const [environmentFilter, setEnvironmentFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<ErrorGroupStatus | ''>('')
  const [assigneeFilter, setAssigneeFilter] = useState<string>('') // '', 'unassigned', 'me', or user_id
  const [totalErrorCount, setTotalErrorCount] = useState<number | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [selectedErrorIds, setSelectedErrorIds] = useState<Set<string>>(new Set())
  const [environments, setEnvironments] = useState<string[]>([])
  const [releases, setReleases] = useState<string[]>([])
  const [releaseFilter, setReleaseFilter] = useState<string>('')
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [muteModalOpen, setMuteModalOpen] = useState(false)
  const [errorToMute, setErrorToMute] = useState<ErrorGroup | null>(null)
  const [activeTab, setActiveTab] = useState<'monitoring' | 'cron' | 'filters' | 'sourcemaps'>(
    'monitoring'
  )

  useEffect(() => {
    if (!id || authLoading || !user) return

    const loadAppData = async () => {
      try {
        // Load core app data - these are required
        const [appData, pingsData, statsData] = await Promise.all([
          appsApi.get(id),
          pingsApi.list(id, { limit: 20 }),
          pingsApi.getStats(id, 24),
        ])
        setApp(appData)
        setPings(pingsData.pings)
        setUptimeStats(statsData)

        // Check total error count to determine if this is a new app with no errors
        try {
          const allErrors = await errorsApi.listGroups(id, { limit: 1 })
          setTotalErrorCount(allErrors.total)
        } catch {
          setTotalErrorCount(0)
        }
      } catch (err) {
        console.error('Failed to load app data:', err)
        toast.error('Failed to load app data')
      } finally {
        setIsLoading(false)
      }
    }

    loadAppData()
  }, [id, authLoading, user])

  // Load environments for filter dropdown
  useEffect(() => {
    if (authLoading || !user) return
    const loadEnvironments = async () => {
      try {
        const envs = await appsApi.getEnvironments()
        setEnvironments(envs)
      } catch (err) {
        console.error('Failed to load environments:', err)
      }
    }
    loadEnvironments()
  }, [authLoading, user])

  // Load releases for filter dropdown
  useEffect(() => {
    if (!id || authLoading || !user) return
    const loadReleases = async () => {
      try {
        const rels = await appsApi.getReleases(id)
        setReleases(rels)
      } catch (err) {
        console.error('Failed to load releases:', err)
      }
    }
    loadReleases()
  }, [id, authLoading, user])

  // Load team members for assignee dropdown
  useEffect(() => {
    if (!teamId || authLoading || !user) return
    const loadTeamMembers = async () => {
      try {
        const response = await teamsApi.getMembers(teamId)
        setTeamMembers(response.members)
      } catch (err) {
        console.error('Failed to load team members:', err)
      }
    }
    loadTeamMembers()
  }, [teamId, authLoading, user])

  // Load errors separately so we can filter by environment, status, and assignee
  useEffect(() => {
    if (!id || authLoading || !user) return

    const loadErrors = async () => {
      try {
        // Determine assignee filter value for API
        let assigned_to: string | undefined = undefined
        if (assigneeFilter === 'unassigned') {
          assigned_to = 'unassigned'
        } else if (assigneeFilter === 'me' && user?.id) {
          assigned_to = user.id
        } else if (assigneeFilter && assigneeFilter !== '') {
          assigned_to = assigneeFilter
        }

        const errorsData = await errorsApi.listGroups(id, {
          status: statusFilter || undefined,
          // Default to showing non-resolved errors if no status filter
          resolved: !statusFilter ? false : undefined,
          limit: 10,
          environment: environmentFilter || undefined,
          release: releaseFilter || undefined,
          assigned_to,
        })
        setErrorGroups(errorsData.error_groups)
      } catch (err) {
        console.error('Failed to load errors:', err)
      }
    }

    loadErrors()
  }, [id, environmentFilter, releaseFilter, statusFilter, assigneeFilter, user, authLoading])

  const handleTriggerPing = async () => {
    if (!id) return
    setIsPinging(true)
    try {
      const result = await pingsApi.trigger(id)
      if (result.is_success) {
        toast.success('Ping successful')
      } else {
        toast.error(result.error_message || 'Ping failed')
      }
      // Refresh pings and stats
      const [pingsData, statsData] = await Promise.all([
        pingsApi.list(id, { limit: 20 }),
        pingsApi.getStats(id, 24),
      ])
      setPings(pingsData.pings)
      setUptimeStats(statsData)
    } catch {
      toast.error('Failed to trigger ping')
    } finally {
      setIsPinging(false)
    }
  }

  const handleStatusChange = async (groupId: string, newStatus: ErrorGroupStatus) => {
    if (!id) return
    try {
      const updated = await errorsApi.updateStatus(id, groupId, newStatus)
      // Update the error in the list or remove it if it no longer matches filter
      if (statusFilter && updated.status !== statusFilter) {
        setErrorGroups(errorGroups.filter(g => g.id !== groupId))
      } else if (!statusFilter && updated.status === 'resolved') {
        // Default view hides resolved errors
        setErrorGroups(errorGroups.filter(g => g.id !== groupId))
      } else {
        setErrorGroups(errorGroups.map(g => (g.id === groupId ? updated : g)))
      }
      toast.success(`Status changed to ${STATUS_LABELS[newStatus]}`)
    } catch {
      toast.error('Failed to update status')
    }
  }

  const handleUpdateApp = async (appId: string, data: AppUpdate) => {
    const updated = await appsApi.update(appId, data)
    setApp(updated)
    toast.success('App settings updated')
  }

  const handleAssign = async (groupId: string, assignedTo: string | null) => {
    if (!id) return
    try {
      const updated = await errorsApi.assign(id, groupId, assignedTo)
      setErrorGroups(errorGroups.map(g => (g.id === groupId ? updated : g)))
      const memberName = assignedTo
        ? teamMembers.find(m => m.user_id === assignedTo)?.name || 'member'
        : null
      toast.success(memberName ? `Assigned to ${memberName}` : 'Unassigned')
    } catch {
      toast.error('Failed to assign error')
    }
  }

  const handleMute = async (options: { hours?: number; until_count?: number }) => {
    if (!id || !errorToMute) return
    try {
      const updated = await errorsApi.mute(id, errorToMute.id, options)
      setErrorGroups(errorGroups.map(g => (g.id === errorToMute.id ? updated : g)))
      const duration = options.hours
        ? `for ${options.hours} hour${options.hours !== 1 ? 's' : ''}`
        : `until ${options.until_count} more occurrences`
      toast.success(`Error muted ${duration}`)
    } catch {
      toast.error('Failed to mute error')
    }
  }

  const openMuteModal = (error: ErrorGroup) => {
    setErrorToMute(error)
    setMuteModalOpen(true)
  }

  const handleBulkStatusChange = async (newStatus: ErrorGroupStatus) => {
    if (!id || selectedErrorIds.size === 0) return
    try {
      const result = await errorsApi.bulkUpdateStatus(id, Array.from(selectedErrorIds), newStatus)
      toast.success(
        `Updated ${result.updated_count} error${result.updated_count !== 1 ? 's' : ''} to ${STATUS_LABELS[newStatus]}`
      )
      // Remove updated errors from list if they no longer match filter
      if (statusFilter && newStatus !== statusFilter) {
        setErrorGroups(errorGroups.filter(g => !selectedErrorIds.has(g.id)))
      } else if (!statusFilter && newStatus === 'resolved') {
        setErrorGroups(errorGroups.filter(g => !selectedErrorIds.has(g.id)))
      } else {
        // Update status in place
        setErrorGroups(
          errorGroups.map(g => (selectedErrorIds.has(g.id) ? { ...g, status: newStatus } : g))
        )
      }
      setSelectedErrorIds(new Set())
    } catch {
      toast.error('Failed to update errors')
    }
  }

  const toggleErrorSelection = (errorId: string) => {
    const newSelected = new Set(selectedErrorIds)
    if (newSelected.has(errorId)) {
      newSelected.delete(errorId)
    } else {
      newSelected.add(errorId)
    }
    setSelectedErrorIds(newSelected)
  }

  const toggleSelectAll = () => {
    if (selectedErrorIds.size === errorGroups.length) {
      setSelectedErrorIds(new Set())
    } else {
      setSelectedErrorIds(new Set(errorGroups.map(g => g.id)))
    }
  }

  if (isLoading) {
    return (
      <>
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-4 w-64 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </>
    )
  }

  if (!app) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">App not found</h2>
          <Link to={`/t/${teamId}/apps`}>
            <Button variant="primary">Back to Apps</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title={app.name}
        subtitle={app.description ?? undefined}
        useHistoryBack
        backLabel="Go back"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Settings
            </Button>
            <Button
              variant="outline"
              onClick={handleTriggerPing}
              disabled={isPinging || !app.health_url}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isPinging ? 'animate-spin' : ''}`} />
              {isPinging ? 'Pinging...' : 'Test Ping'}
            </Button>
          </div>
        }
      />
      {app.health_url && (
        <p className="text-slate-500 text-sm -mt-6 mb-8">Monitoring: {app.health_url}</p>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card variant="stat" className="p-4">
          <div className="flex items-center gap-2 mb-2">
            {uptimeStats?.current_status === null ? (
              <Clock className="w-5 h-5 text-slate-500" />
            ) : uptimeStats?.current_status ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
            <span className="text-sm text-slate-500">Status</span>
          </div>
          <p className="text-lg font-bold text-slate-900">
            {uptimeStats?.current_status === null
              ? 'No pings'
              : uptimeStats?.current_status
                ? 'Healthy'
                : 'Down'}
          </p>
        </Card>

        <Card variant="stat" className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-slate-500" />
            <span className="text-sm text-slate-500">Uptime (24h)</span>
          </div>
          <p className="text-lg font-bold text-slate-900">
            {uptimeStats?.uptime_percentage != null
              ? `${uptimeStats.uptime_percentage.toFixed(1)}%`
              : '-'}
          </p>
        </Card>

        <Card variant="stat" className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-slate-500" />
            <span className="text-sm text-slate-500">Avg Response</span>
          </div>
          <p className="text-lg font-bold text-slate-900">
            {uptimeStats?.avg_response_time_ms != null
              ? `${Math.round(uptimeStats.avg_response_time_ms)}ms`
              : '-'}
          </p>
        </Card>

        <Card variant="stat" className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-slate-500" />
            <span className="text-sm text-slate-500">Unresolved Errors</span>
          </div>
          <p
            className={`text-lg font-bold ${errorGroups.length > 0 ? 'text-red-600' : 'text-slate-900'}`}
          >
            {errorGroups.length}
          </p>
        </Card>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-4 border-b border-slate-200 mb-6">
        <button
          onClick={() => setActiveTab('monitoring')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'monitoring'
              ? 'border-accent-500 text-accent-600'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
          }`}
        >
          <span className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Errors & Uptime
          </span>
        </button>
        <button
          onClick={() => setActiveTab('cron')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'cron'
              ? 'border-accent-500 text-accent-600'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
          }`}
        >
          <span className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Cron Jobs
          </span>
        </button>
        <button
          onClick={() => setActiveTab('filters')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'filters'
              ? 'border-accent-500 text-accent-600'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
          }`}
        >
          <span className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Inbound Filters
          </span>
        </button>
        <button
          onClick={() => setActiveTab('sourcemaps')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'sourcemaps'
              ? 'border-accent-500 text-accent-600'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
          }`}
        >
          <span className="flex items-center gap-2">
            <FileCode className="w-4 h-4" />
            Source Maps
          </span>
        </button>
      </div>

      {activeTab === 'cron' ? (
        <CronMonitorsList appId={id!} />
      ) : activeTab === 'filters' ? (
        <FiltersList appId={id!} />
      ) : activeTab === 'sourcemaps' ? (
        <SourceMapsList appId={id!} />
      ) : (
        <>
          {/* Show WaitingForFirstError when app has no errors at all */}
          {totalErrorCount === 0 && !statusFilter && !environmentFilter ? (
            <div className="mb-6">
              <WaitingForFirstError appName={app.name} docsPath="/docs" />
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Errors section */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  {statusFilter ? `${STATUS_LABELS[statusFilter]} Errors` : 'Unresolved Errors'}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setAssigneeFilter(assigneeFilter === 'me' ? '' : 'me')}
                    className={`text-sm px-3 py-1 rounded-lg border flex items-center gap-1.5 transition-colors ${
                      assigneeFilter === 'me'
                        ? 'bg-accent-50 border-accent-300 text-accent-700 font-medium'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <User className="w-3.5 h-3.5" />
                    My Issues
                  </button>
                  <span className="text-slate-300">|</span>
                  <Filter className="w-4 h-4 text-slate-400" />
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value as ErrorGroupStatus | '')}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white"
                  >
                    <option value="">Unresolved</option>
                    {ERROR_GROUP_STATUSES.map(s => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </option>
                    ))}
                  </select>
                  <select
                    value={environmentFilter}
                    onChange={e => setEnvironmentFilter(e.target.value)}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white"
                  >
                    <option value="">All Envs</option>
                    {environments.map(env => (
                      <option key={env} value={env}>
                        {env}
                      </option>
                    ))}
                  </select>
                  {releases.length > 0 && (
                    <select
                      value={releaseFilter}
                      onChange={e => setReleaseFilter(e.target.value)}
                      className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white"
                    >
                      <option value="">All Releases</option>
                      {releases.map(rel => (
                        <option key={rel} value={rel}>
                          {rel}
                        </option>
                      ))}
                    </select>
                  )}
                  <select
                    value={assigneeFilter}
                    onChange={e => setAssigneeFilter(e.target.value)}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white"
                  >
                    <option value="">All Assignees</option>
                    <option value="unassigned">Unassigned</option>
                    <option value="me">Assigned to Me</option>
                    {teamMembers
                      .filter(m => m.user_id !== user?.id)
                      .map(member => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Bulk action bar */}
              {errorGroups.length > 0 && (
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-200">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
                  >
                    {selectedErrorIds.size === errorGroups.length ? (
                      <CheckSquare className="w-4 h-4 text-accent-600" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    {selectedErrorIds.size > 0 ? `${selectedErrorIds.size} selected` : 'Select all'}
                  </button>
                  {selectedErrorIds.size > 0 && (
                    <>
                      <span className="text-slate-300">|</span>
                      <span className="text-sm text-slate-500">Set status:</span>
                      {ERROR_GROUP_STATUSES.map(s => (
                        <button
                          key={s}
                          onClick={() => handleBulkStatusChange(s)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${STATUS_COLORS[s]} hover:opacity-80`}
                        >
                          {STATUS_LABELS[s]}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
              {errorGroups.length === 0 ? (
                <p className="text-slate-500 text-sm">
                  {totalErrorCount === 0
                    ? 'No errors received yet'
                    : statusFilter
                      ? `No ${STATUS_LABELS[statusFilter].toLowerCase()} errors`
                      : environmentFilter
                        ? `No unresolved errors in ${environmentFilter}`
                        : 'No unresolved errors'}
                </p>
              ) : (
                <div className="space-y-3">
                  {errorGroups.map(group => (
                    <div
                      key={group.id}
                      className={`bg-slate-50 rounded-lg border transition-colors ${
                        selectedErrorIds.has(group.id)
                          ? 'border-accent-400 bg-accent-50/50'
                          : 'border-slate-200 hover:border-accent-300 hover:bg-accent-50/30'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 p-3">
                        <button
                          onClick={() => toggleErrorSelection(group.id)}
                          className="flex-shrink-0 mt-0.5"
                        >
                          {selectedErrorIds.has(group.id) ? (
                            <CheckSquare className="w-4 h-4 text-accent-600" />
                          ) : (
                            <Square className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                          )}
                        </button>
                        <Link
                          to={`/t/${teamId}/apps/${id}/errors/${group.id}`}
                          className="flex-1 min-w-0 cursor-pointer group"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-accent-600 flex-shrink-0 transition-colors" />
                            <p className="text-slate-900 font-medium truncate group-hover:text-accent-700">
                              {group.message}
                            </p>
                            {group.status === 'muted' && (
                              <span title="Muted">
                                <BellOff className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                              </span>
                            )}
                            {group.status === 'ignored' && (
                              <span title="Ignored">
                                <EyeOff className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                              </span>
                            )}
                            {group.status === 'regressed' && (
                              <span title="Regressed">
                                <RotateCcw className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
                            <Badge variant="error">{group.level}</Badge>
                            <span
                              className={`px-1.5 py-0.5 rounded text-xs ${STATUS_COLORS[group.status]}`}
                            >
                              {STATUS_LABELS[group.status]}
                            </span>
                            <span>{group.occurrence_count} occurrences</span>
                            {group.users_affected_count > 0 && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {group.users_affected_count} user
                                {group.users_affected_count !== 1 ? 's' : ''}
                              </span>
                            )}
                            {group.assigned_to && (
                              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                                <UserCheck className="w-3 h-3" />
                                {teamMembers.find(m => m.user_id === group.assigned_to)?.name ||
                                  'Assigned'}
                              </span>
                            )}
                            {group.environments?.length > 0 && (
                              <div className="flex gap-1">
                                {group.environments.map(env => (
                                  <span
                                    key={env}
                                    className={`px-1.5 py-0.5 rounded text-xs ${
                                      env === 'production'
                                        ? 'bg-red-100 text-red-700'
                                        : env === 'staging'
                                          ? 'bg-amber-100 text-amber-700'
                                          : 'bg-slate-100 text-slate-600'
                                    }`}
                                  >
                                    {env}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </Link>
                        <div className="flex flex-col gap-1">
                          <select
                            value={group.status}
                            onChange={e =>
                              handleStatusChange(group.id, e.target.value as ErrorGroupStatus)
                            }
                            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                          >
                            {ERROR_GROUP_STATUSES.map(s => (
                              <option key={s} value={s}>
                                {STATUS_LABELS[s]}
                              </option>
                            ))}
                          </select>
                          <select
                            value={group.assigned_to || ''}
                            onChange={e => handleAssign(group.id, e.target.value || null)}
                            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                          >
                            <option value="">Unassigned</option>
                            {teamMembers.map(member => (
                              <option key={member.user_id} value={member.user_id}>
                                {member.name}
                                {member.user_id === user?.id ? ' (me)' : ''}
                              </option>
                            ))}
                          </select>
                          {group.status !== 'muted' && (
                            <button
                              onClick={() => openMuteModal(group)}
                              className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-colors flex items-center gap-1"
                              title="Mute notifications"
                            >
                              <BellOff className="w-3 h-3" />
                              Mute
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Recent pings section */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent Pings</h3>
              {pings.length === 0 ? (
                <p className="text-slate-500 text-sm">No pings yet</p>
              ) : (
                <div className="space-y-2">
                  {pings.slice(0, 10).map(ping => (
                    <div key={ping.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {ping.is_success ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600" />
                        )}
                        <span className="text-slate-900">
                          {ping.status_code || (ping.is_success ? 'OK' : 'Failed')}
                        </span>
                        {ping.response_time_ms && (
                          <span className="text-slate-500">{ping.response_time_ms}ms</span>
                        )}
                      </div>
                      <span className="text-slate-500 text-xs">
                        {new Date(ping.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Regional Status Map */}
            {app.health_url && (
              <div className="lg:col-span-2">
                <RegionalStatusMap appId={id!} hours={24} />
              </div>
            )}

            {/* Infrastructure Metrics Dashboard */}
            {app.metrics_url && (
              <div className="lg:col-span-2">
                <MetricsDashboard appId={id!} />
              </div>
            )}
          </div>
        </>
      )}

      <AppSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        app={app}
        onSubmit={handleUpdateApp}
      />

      <MuteErrorModal
        isOpen={muteModalOpen}
        onClose={() => {
          setMuteModalOpen(false)
          setErrorToMute(null)
        }}
        errorMessage={errorToMute?.message || ''}
        onMute={handleMute}
      />
    </>
  )
}
